<script setup lang="ts">
import { useDashboardStats } from '@/composables/useDashboardStats';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { lastCommerceId } from '@/refs/commerce.refs';

const {} = useDashboardStats();
const router = useRouter();
const { t } = useI18n({ useScope: 'global' });

function goToNewUser() {
  const id = lastCommerceId.value;
  if (id) {
    router.push({
      path: `/commerces/${id}`,
      query: { tab: 'users', action: 'createUser' },
    });
  } else {
    router.push({ path: '/commerces', query: { action: 'createUser' } });
  }
}
</script>

<template>
  <section class="hero">
    <div class="hero__text">
      <h2 class="hero__title">
        {{ t('dashboard.hero.title') }} <span class="hero__slash">/</span>
        <span class="hero__brand-wrap">
          <span class="hero__brand">Nextepay</span>
          <span class="hero__underline" />
        </span>
      </h2>
      <p class="hero__sub">{{ t('dashboard.hero.sub') }}</p>
    </div>
    <div class="hero__actions">
      <button class="hero__btn hero__btn--secondary" @click="goToNewUser">
        <i class="pi pi-user" />
        <span>{{ t('dashboard.hero.newUser') }}</span>
      </button>
      <button
        class="hero__btn hero__btn--primary"
        @click="router.push('/clients/new')"
      >
        <i class="pi pi-building" />
        <span>{{ t('dashboard.hero.newClient') }}</span>
      </button>
    </div>
  </section>
</template>

<style scoped>
.hero {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}
@media (min-width: 1024px) {
  .hero {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: 2rem;
  }
}

.hero__text {
  flex: 1;
  min-width: 0;
}

.hero__title {
  margin: 0 0 0.625rem;
  font-size: clamp(2.25rem, 4vw, 3rem);
  font-weight: 800;
  letter-spacing: -0.03em;
  color: #0b1320;
  line-height: 1.1;
}

.hero__slash {
  color: rgba(11, 19, 32, 0.2);
  font-weight: 300;
}

.hero__brand-wrap {
  position: relative;
  display: inline-block;
}
.hero__brand {
  color: #0b1320;
}
.hero__underline {
  position: absolute;
  bottom: -0.25rem;
  left: 0;
  width: 100%;
  height: 4px;
  border-radius: 9999px;
  background: rgba(255, 214, 10, 0.4);
}

.hero__sub {
  margin: 0;
  font-size: 1.0625rem;
  line-height: 1.7;
  color: rgba(11, 19, 32, 0.6);
  max-width: 40rem;
}

.hero__actions {
  display: flex;
  gap: 0.75rem;
  flex-shrink: 0;
  flex-wrap: wrap;
}
@media (max-width: 479px) {
  .hero__actions {
    width: 100%;
  }
}

.hero__btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  height: 2.875rem;
  padding: 0 1.5rem;
  border-radius: 0.75rem;
  border: none;
  font-size: 0.875rem;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  white-space: nowrap;
  flex: 1;
  justify-content: center;
  transition: all 0.25s ease;
}
@media (min-width: 480px) {
  .hero__btn {
    flex: unset;
  }
}
.hero__btn:hover {
  transform: translateY(-1px);
}

.hero__btn--primary {
  background: #ffd60a;
  color: #0b1320;
}
.hero__btn--primary:hover {
  background: #e6c109;
  box-shadow: 0 10px 20px -5px rgba(255, 214, 10, 0.4);
}

.hero__btn--secondary {
  background: #fff;
  color: #0b1320;
  border: 1px solid #e2e8f0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}
.hero__btn--secondary:hover {
  box-shadow: 0 4px 12px rgba(11, 19, 32, 0.08);
}
</style>

<style>
[data-theme='dark'] .hero__title,
[data-theme='dark'] .hero__brand {
  color: #f1f5f9;
}
[data-theme='dark'] .hero__slash {
  color: rgba(241, 245, 249, 0.18);
}
[data-theme='dark'] .hero__sub {
  color: rgba(241, 245, 249, 0.45);
}
[data-theme='dark'] .hero__btn--secondary {
  background: rgba(255, 255, 255, 0.07);
  border-color: rgba(255, 255, 255, 0.1);
  color: #f1f5f9;
}
</style>
